import Vue from 'vue'
import httpService from '@/core/http-service'
import { filter, isEqual, cloneDeep, sumBy } from 'lodash'
import cartService from '@/core/cart-service'
import { productStatuses } from '@/variables/product-statuses'

const state = {
  shoppingLists: null,
  createdByList: null,
  createdByFilter: null,
  selectedShoppingListId: null,
  currentShoppingList: null,
  pristineCurrentShoppingList: null,
  selectedRows: [],
  filter: ''
}

const mutations = {
  set_shopping_lists(state, payload) {
    state.shoppingLists = payload
  },
  set_created_by_filter(state, value) {
    state.createdByFilter = value
  },
  set_created_by_list(state, payload) {
    state.createdByList = payload
  },
  set_selected_shopping_list_id(state, payload) {
    state.selectedShoppingListId = payload
  },
  set_current_shopping_list(state, payload) {
    state.currentShoppingList = payload
    state.pristineCurrentShoppingList = cloneDeep(payload)
  },
  set_current_shopping_list_property(state, { property, value }) {
    Vue.set(state.currentShoppingList, property, value)
  },
  set_row_property(state, { rowId, property, value }) {
    const row = state.currentShoppingList.rows.find(row => row.id === rowId)
    if(row) Vue.set(row, property, value)
  },
  add_row(state, row) {
    state.currentShoppingList.rows.push(row)
  },
  delete_row(state, rowId) {
    const i = state.currentShoppingList.rows.findIndex(row => row.id === rowId)
    if(i !== -1) state.currentShoppingList.rows.splice(i, 1)
  },
  remove_selected_row(state, rowId) {
    const i = state.selectedRows.findIndex(row => row === rowId)
    if(i !== -1) state.selectedRows.splice(i, 1)
  },
  set_selected_rows(state, payload) {
    state.selectedRows = payload
  },
  set_filter(state, payload) {
    state.filter = payload
  }
}

const actions = {
  async getShoppingLists({ commit }) {
    const response = await httpService.get('shoppinglist/loadAll')
    commit('set_shopping_lists', response.data.shoppingLists)
    commit('set_created_by_list', response.data.createdByList)
  },
  async selectShoppingList({ commit }, shoppingListId) {
    commit('set_selected_shopping_list_id', shoppingListId)
  },
  async getSelectedShoppingList({ commit, getters }) {
    const response = await httpService.get(`shoppinglist/${getters.selectedShoppingListId}`)
    commit('set_current_shopping_list', response.data)
  },
  setCreatedByFilter({ commit }, value) {
    commit('set_created_by_filter', value)
  },
  resetSelectedShoppingList({ commit }) {
    commit('set_selected_shopping_list_id', null)
    commit('set_current_shopping_list', null)
    commit('set_selected_rows', [])
    commit('set_created_by_list', null)
  },
  addRow({ commit }, row) {
    commit('add_row', row)
  },
  async addRows({ dispatch, getters }, rows) {
    dispatch('updateCurrentShoppingListProperty', { property: 'rows', value: cartService.mergeRows(getters.currentShoppingList.rows, rows) })
  },
  updateRow({ commit }, { row, property, value }) {
    commit('set_row_property', { rowId: row, property, value })
  },
  deleteRow({ commit, state }, rowId) {
    commit('delete_row', rowId)
    commit('remove_selected_row', rowId)
  },
  async deleteSelectedRows({ getters, dispatch }) {
    const currentSelectedRows = [...getters.selectedRows]
    for (const row of currentSelectedRows) {
      await dispatch('deleteRow', row)
    }
  },
  async saveCurrentShoppingList({ getters, commit }) {
    const response = await httpService.put(`shoppinglist/${getters.currentShoppingList.id}`, getters.currentShoppingList)
    commit('set_current_shopping_list', response.data)
  },
  deleteShoppingList({ commit }, shoppingListId) {
    return httpService.delete(`shoppinglist/${shoppingListId}`)
  },
  addNewShoppingList({ commit }, shoppingListName) {
    return httpService.post('shoppinglist', { name: shoppingListName, rows: [] })
  },
  updateCurrentShoppingListProperty({ commit }, { property, value }) {
    commit('set_current_shopping_list_property', { property, value })
  },
  addCurrentShoppingListToCart({ getters, dispatch }) {
    // Filter out disabled items before adding to cart
    const enabledRows = getters.currentShoppingList.rows.filter(row => !isDisabledShoppingListItem(row))
    dispatch('commerce/addListToCart', { ...getters.currentShoppingList, rows: enabledRows }, { root: true })
  },
  addSelectedRowsToCart({ getters, dispatch }) {
    const selectedRows = filter(getters.currentShoppingList.rows, row => getters.selectedRows.includes(row.id))
    // Filter out disabled items from selected rows before adding to cart
    const enabledSelectedRows = selectedRows.filter(row => !isDisabledShoppingListItem(row))
    dispatch('commerce/addListToCart', { ...getters.currentShoppingList, rows: enabledSelectedRows }, { root: true })
  },
  setSelectedRows({ commit }, rows) {
    commit('set_selected_rows', rows)
  },
  setFilter({ commit }, filter) {
    commit('set_filter', filter)
  }

}

// Helper function to check if a shopping list item is disabled
const isDisabledShoppingListItem = (row) => {
  // Check both publicationStatusCode and publicationStatus properties
  const statusCode = row.publicationStatusCode || row.publicationStatus

  // If statusCode is null/undefined, treat as disabled
  if (statusCode == null) {
    return true
  }

  // Check if the item has publication status 2 (discontinued) or 6 (expired)
  const hasDisabledStatus = statusCode === productStatuses.discontinued ||
                           statusCode === productStatuses.expired

  // Check if the item has no price or zero price
  const hasNoPrice = !row.price || row.price === 0 || row.price === '0'

  return hasDisabledStatus || (hasNoPrice && statusCode !== productStatuses.noPrice) || row.isLongDelivery
}

const getters = {
  createdByList: state => state.createdByList,
  createdByFilter: state => state.createdByFilter || null,
  shoppingLists: state => state.shoppingLists,
  selectedShoppingListId: state => state.selectedShoppingListId,
  currentShoppingList: state => state.currentShoppingList,
  selectedRows: state => state.selectedRows,
  currentShoppingListHasChanges: state => !isEqual(state.currentShoppingList, state.pristineCurrentShoppingList),
  filter: state => state.filter,
  filteredShoppingLists: state => {
    if(!state.filter) return state.shoppingLists
    return filter(state.shoppingLists, list => list.searchText.toLowerCase().includes(state.filter.toLowerCase()))
  },
  totalPriceCurrentShoppingList: state => {
    if (!state.currentShoppingList || !state.currentShoppingList.rows) return 0
    return sumBy(state.currentShoppingList.rows, row => {
      // Exclude disabled items and quote items from price calculation
      if (isDisabledShoppingListItem(row)) return 0

      // Check both publicationStatusCode and publicationStatus properties like cart does
      const statusCode = row.publicationStatusCode || row.publicationStatus
      // Exclude quote items (items with publicationStatus === productStatuses.noPrice)
      if (statusCode === productStatuses.noPrice) return 0
      return row.price * row.quantity
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
